/**
 * Column histogram — partitions in-memory column values into equal-width or
 * quantile buckets. Pure function, no Electron dependencies.
 *
 * Used by:
 * - ColumnProfileBadge popover (8 buckets, equal-width default)
 * - DataProfileHistogramCard (configurable: 8/16/32 buckets, equal-width or quantile)
 */

export type HistogramMode = 'equal-width' | 'quantile';

export type HistogramBucket = {
  /** Numeric for numeric cols, ISO date string for date cols. */
  rangeStart: number | string;
  rangeEnd: number | string;
  count: number;
};

export type HistogramResult = {
  buckets: HistogramBucket[];
  /** Rows where the column was NULL or undefined. */
  nullCount: number;
  /** Rows where the column existed but couldn't be parsed as numeric/date. */
  nonNumericCount: number;
};

const DATE_TYPES = new Set([
  'date',
  'datetime',
  'datetime2',
  'datetimeoffset',
  'smalldatetime',
  'timestamp',
  'timestamptz',
  'timestamp with time zone',
  'timestamp without time zone',
]);

function isDateDataType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const base = dataType.toLowerCase().replace(/\(.*\)/, '').trim();
  if (DATE_TYPES.has(base)) return true;
  return base.startsWith('datetime') || base.startsWith('timestamp');
}

function tryParseNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function tryParseDate(value: unknown): number | null {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  return null;
}

function formatDateBoundary(epochMs: number): string {
  const d = new Date(epochMs);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * Compute a histogram of `bucketCount` buckets over the column's non-null values.
 *
 * - Numeric columns: equal-width = (max - min) / N; quantile = N equal-count groups.
 * - Date columns: detected via `dataType` hint or by parseability; bucketed as epoch ms.
 * - Mixed numeric/string: treats as unsupported (returns empty buckets, all rows in nonNumericCount).
 * - Single distinct value: one bucket containing all rows.
 */
export function computeColumnHistogram(
  rows: Record<string, unknown>[],
  columnId: string,
  bucketCount: number,
  mode: HistogramMode,
  dataType?: string,
  /**
   * Optional axis-trim bounds. When set, rows whose value falls outside
   * [axisMin, axisMax] are excluded from bucketing AND the bucket bounds use
   * [axisMin, axisMax] instead of the data's [min, max]. Mirrors the SQL-side
   * `axisMin`/`axisMax` in `ProfilingSqlOpts`.
   */
  axisMin?: number,
  axisMax?: number,
): HistogramResult {
  const safeBucketCount = Math.max(1, Math.floor(bucketCount));
  let nullCount = 0;
  let nonNumericCount = 0;

  const useDateMode = isDateDataType(dataType);
  const trimActive =
    typeof axisMin === 'number' &&
    typeof axisMax === 'number' &&
    Number.isFinite(axisMin) &&
    Number.isFinite(axisMax) &&
    axisMax > axisMin;
  const numericValues: number[] = [];

  for (let i = 0; i < rows.length; i++) {
    const value = rows[i]?.[columnId];
    if (value === null || value === undefined) {
      nullCount++;
      continue;
    }
    const parsed = useDateMode ? tryParseDate(value) : tryParseNumber(value);
    if (parsed === null) {
      nonNumericCount++;
      continue;
    }
    if (trimActive && (parsed < (axisMin as number) || parsed > (axisMax as number))) {
      continue;
    }
    numericValues.push(parsed);
  }

  if (numericValues.length === 0) {
    return { buckets: [], nullCount, nonNumericCount };
  }

  const formatBoundary = (n: number): number | string =>
    useDateMode ? formatDateBoundary(n) : n;

  // Use trimmed bounds for bucket math when active; otherwise derive from data.
  let min: number;
  let max: number;
  if (trimActive) {
    min = axisMin as number;
    max = axisMax as number;
  } else {
    min = numericValues[0];
    max = numericValues[0];
    for (let i = 1; i < numericValues.length; i++) {
      const v = numericValues[i];
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }

  if (min === max) {
    return {
      buckets: [
        {
          rangeStart: formatBoundary(min),
          rangeEnd: formatBoundary(max),
          count: numericValues.length,
        },
      ],
      nullCount,
      nonNumericCount,
    };
  }

  if (mode === 'equal-width') {
    const width = (max - min) / safeBucketCount;
    const buckets: HistogramBucket[] = [];
    const counts = new Int32Array(safeBucketCount);
    for (let i = 0; i < numericValues.length; i++) {
      const v = numericValues[i];
      let idx = Math.floor((v - min) / width);
      if (idx >= safeBucketCount) idx = safeBucketCount - 1;
      if (idx < 0) idx = 0;
      counts[idx]++;
    }
    for (let i = 0; i < safeBucketCount; i++) {
      const start = min + i * width;
      const end = i === safeBucketCount - 1 ? max : min + (i + 1) * width;
      buckets.push({
        rangeStart: formatBoundary(start),
        rangeEnd: formatBoundary(end),
        count: counts[i],
      });
    }
    return { buckets, nullCount, nonNumericCount };
  }

  // mode === 'quantile' — sort values, partition into N equal-count groups.
  const sorted = numericValues.slice().sort((a, b) => a - b);
  const total = sorted.length;
  const buckets: HistogramBucket[] = [];
  for (let i = 0; i < safeBucketCount; i++) {
    const startIdx = Math.floor((i * total) / safeBucketCount);
    const endIdx =
      i === safeBucketCount - 1
        ? total
        : Math.floor(((i + 1) * total) / safeBucketCount);
    const count = endIdx - startIdx;
    if (count === 0) continue;
    const startVal = sorted[startIdx];
    const endVal = sorted[endIdx - 1];
    buckets.push({
      rangeStart: formatBoundary(startVal),
      rangeEnd: formatBoundary(endVal),
      count,
    });
  }
  return { buckets, nullCount, nonNumericCount };
}
